import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRoute, useLocation } from 'wouter';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { PartnerAccessService } from '@/services/PartnerAccessService';
import { apiJson } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function TabQueryState({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyMessage,
  colSpan,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyMessage: string;
  colSpan: number;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} className="py-8 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        </TableCell>
      </TableRow>
    );
  }
  if (isError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} className="py-8 text-center text-red-600">
          {queryErrorMessage(error, 'Failed to load data.')}
        </TableCell>
      </TableRow>
    );
  }
  if (isEmpty) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} className="py-8 text-center text-muted-foreground">
          {emptyMessage}
        </TableCell>
      </TableRow>
    );
  }
  return <>{children}</>;
}

export default function PartnerAccessOrgDetailPage() {
  const [, params] = useRoute('/admin/partner-access/:organisationId');
  const [, setLocation] = useLocation();
  const organisationId = Number(params?.organisationId);
  const queryClient = useQueryClient();
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const orgQuery = useQuery({
    queryKey: ['partner-access', 'organisation', organisationId],
    queryFn: async () => {
      const response = await PartnerAccessService.getOrganisation(organisationId);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load organisation');
      }
      return response;
    },
    enabled: Number.isFinite(organisationId) && organisationId > 0,
  });

  const grantsQuery = useQuery({
    queryKey: ['partner-access', 'grants', organisationId],
    queryFn: async () => {
      const response = await PartnerAccessService.listGrants(organisationId);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load retailer grants');
      }
      return response;
    },
    enabled: Number.isFinite(organisationId) && organisationId > 0,
  });

  const membershipsQuery = useQuery({
    queryKey: ['partner-access', 'memberships', organisationId],
    queryFn: async () => {
      const response = await PartnerAccessService.listMemberships(organisationId);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load memberships');
      }
      return response;
    },
    enabled: Number.isFinite(organisationId) && organisationId > 0,
  });

  const auditQuery = useQuery({
    queryKey: ['partner-access', 'audit', organisationId],
    queryFn: async () => {
      const response = await PartnerAccessService.listAuditEvents(organisationId);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load audit events');
      }
      return response;
    },
    enabled: Number.isFinite(organisationId) && organisationId > 0,
  });

  const rolesQuery = useQuery({
    queryKey: ['partner-access', 'roles'],
    queryFn: async () => {
      const response = await PartnerAccessService.listRoles();
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to load portal roles');
      }
      return response;
    },
  });

  const retailersQuery = useQuery({
    queryKey: ['retailers'],
    queryFn: () => apiJson<Array<{ id: number; name: string }>>('/api/retailers'),
    enabled: grantDialogOpen,
  });

  const userSearchQuery = useQuery({
    queryKey: ['partner-access', 'user-search', userSearch],
    queryFn: async () => {
      const response = await PartnerAccessService.searchUsers(userSearch);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to search users');
      }
      return response;
    },
    enabled: memberDialogOpen && userSearch.trim().length >= 2,
  });

  const invalidateOrg = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-access', 'organisation', organisationId] });
    queryClient.invalidateQueries({ queryKey: ['partner-access', 'organisations'] });
  };

  const grantMutation = useMutation({
    mutationFn: (retailerId: number) => PartnerAccessService.addGrant(organisationId, retailerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-access', 'grants', organisationId] });
      invalidateOrg();
      setGrantDialogOpen(false);
      setSelectedRetailerId('');
    },
  });

  const grantStatusMutation = useMutation({
    mutationFn: ({ grantId, status }: { grantId: number; status: 'active' | 'revoked' }) =>
      PartnerAccessService.setGrantStatus(organisationId, grantId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-access', 'grants', organisationId] });
      invalidateOrg();
    },
  });

  const membershipMutation = useMutation({
    mutationFn: (userId: number) => PartnerAccessService.addMembership(organisationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-access', 'memberships', organisationId] });
      invalidateOrg();
      setMemberDialogOpen(false);
      setUserSearch('');
      setSelectedUserId(null);
    },
  });

  const membershipStatusMutation = useMutation({
    mutationFn: ({ membershipId, status }: { membershipId: number; status: 'active' | 'revoked' }) =>
      PartnerAccessService.setMembershipStatus(membershipId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-access', 'memberships', organisationId] });
      invalidateOrg();
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: 'active' | 'inactive') =>
      PartnerAccessService.updateOrganisation(organisationId, { status }),
    onSuccess: () => {
      invalidateOrg();
    },
  });

  const pageMutationError =
    grantStatusMutation.error instanceof Error ? grantStatusMutation.error.message
      : membershipMutation.error instanceof Error ? membershipMutation.error.message
        : membershipStatusMutation.error instanceof Error ? membershipStatusMutation.error.message
          : statusMutation.error instanceof Error ? statusMutation.error.message
            : null;

  const organisation = orgQuery.data?.data?.organisation;
  const grants = grantsQuery.data?.data?.grants ?? [];
  const memberships = membershipsQuery.data?.data?.memberships ?? [];
  const auditEvents = auditQuery.data?.data?.events ?? [];
  const retailerViewerRole = rolesQuery.data?.data?.roles.find((role) => role.key === 'RetailerViewer');

  if (orgQuery.isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (orgQuery.isError) {
    return (
      <div className="space-y-4 p-6">
        <p className="text-red-600">
          {orgQuery.error instanceof Error ? orgQuery.error.message : 'Failed to load organisation.'}
        </p>
        <Button variant="link" onClick={() => setLocation('/admin/partner-access')}>
          Back to partner access
        </Button>
      </div>
    );
  }

  if (!organisation) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Organisation not found.</p>
        <Button variant="link" onClick={() => setLocation('/admin/partner-access')}>
          Back to partner access
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Button
        variant="ghost"
        className="pl-0"
        onClick={() => setLocation('/admin/partner-access')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to partner access
      </Button>

      <PageHeader
        title={organisation.name}
        description={`Portal key: ${organisation.key}`}
      >
        <div className="flex items-center gap-2">
          <Badge variant={organisation.status === 'active' ? 'default' : 'secondary'}>
            {organisation.status}
          </Badge>
          {organisation.status === 'active' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => statusMutation.mutate('inactive')}
              disabled={statusMutation.isPending}
            >
              Deactivate org
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => statusMutation.mutate('active')}
              disabled={statusMutation.isPending}
            >
              Activate org
            </Button>
          )}
        </div>
      </PageHeader>

      {pageMutationError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {pageMutationError}
        </p>
      ) : null}

      <Tabs defaultValue="grants">
        <TabsList>
          <TabsTrigger value="grants">Retailer grants</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="roles">Effective permissions</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="grants" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setGrantDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add retailer grant
            </Button>
          </div>
          <div className="rounded-md border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Retailer</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                <TabQueryState
                  isLoading={grantsQuery.isLoading}
                  isError={grantsQuery.isError}
                  error={grantsQuery.error}
                  isEmpty={grants.length === 0}
                  emptyMessage="No retailer grants yet."
                  colSpan={4}
                >
                  {grants.map((grant) => (
                    <TableRow key={grant.id}>
                      <TableCell>{grant.retailerName}</TableCell>
                      <TableCell>{grant.accessLevel}</TableCell>
                      <TableCell>
                        <Badge variant={grant.status === 'active' ? 'default' : 'secondary'}>
                          {grant.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {grant.status === 'active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => grantStatusMutation.mutate({ grantId: grant.id, status: 'revoked' })}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => grantStatusMutation.mutate({ grantId: grant.id, status: 'active' })}
                          >
                            Restore
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TabQueryState>
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="members" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setMemberDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add member
            </Button>
          </div>
          <div className="rounded-md border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Access mode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                <TabQueryState
                  isLoading={membershipsQuery.isLoading}
                  isError={membershipsQuery.isError}
                  error={membershipsQuery.error}
                  isEmpty={memberships.length === 0}
                  emptyMessage="No portal members yet."
                  colSpan={6}
                >
                  {memberships.map((membership) => (
                    <TableRow key={membership.id}>
                      <TableCell>{membership.userName}</TableCell>
                      <TableCell>{membership.userEmail}</TableCell>
                      <TableCell>{membership.roleKeys.join(', ') || '—'}</TableCell>
                      <TableCell>{membership.accessMode}</TableCell>
                      <TableCell>
                        <Badge variant={membership.status === 'active' ? 'default' : 'secondary'}>
                          {membership.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {membership.status === 'active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => membershipStatusMutation.mutate({
                              membershipId: membership.id,
                              status: 'revoked',
                            })}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => membershipStatusMutation.mutate({
                              membershipId: membership.id,
                              status: 'active',
                            })}
                          >
                            Restore
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TabQueryState>
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <div className="rounded-md border bg-white p-4">
            <p className="text-sm text-muted-foreground">
              New members receive the <strong>RetailerViewer</strong> role automatically.
              Per-permission toggles are not editable in v1.
            </p>
            {rolesQuery.isLoading ? (
              <div className="mt-4 flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : rolesQuery.isError ? (
              <p className="mt-4 text-sm text-red-600">
                {queryErrorMessage(rolesQuery.error, 'Failed to load portal roles.')}
              </p>
            ) : retailerViewerRole ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {retailerViewerRole.permissionKeys.map((permission) => (
                  <Badge key={permission} variant="outline">{permission}</Badge>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">RetailerViewer role not found.</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="rounded-md border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead>Resource</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TabQueryState
                  isLoading={auditQuery.isLoading}
                  isError={auditQuery.isError}
                  error={auditQuery.error}
                  isEmpty={auditEvents.length === 0}
                  emptyMessage="No portal audit events yet."
                  colSpan={4}
                >
                  {auditEvents.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{new Date(event.occurredAt).toLocaleString()}</TableCell>
                      <TableCell>{event.action}</TableCell>
                      <TableCell>{event.decision}</TableCell>
                      <TableCell>
                        {event.resourceType ? `${event.resourceType}:${event.resourceId ?? '—'}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TabQueryState>
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add retailer grant</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Retailer</Label>
            {retailersQuery.isLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading retailers…
              </div>
            ) : retailersQuery.isError ? (
              <p className="text-sm text-red-600">
                {queryErrorMessage(retailersQuery.error, 'Failed to load retailers.')}
              </p>
            ) : (
              <Select value={selectedRetailerId} onValueChange={setSelectedRetailerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select retailer" />
                </SelectTrigger>
                <SelectContent>
                  {(retailersQuery.data ?? []).map((retailer) => (
                    <SelectItem key={retailer.id} value={String(retailer.id)}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            {grantMutation.error instanceof Error ? (
              <p className="mr-auto text-sm text-red-600">{grantMutation.error.message}</p>
            ) : null}
            <Button
              onClick={() => grantMutation.mutate(Number(selectedRetailerId))}
              disabled={!selectedRetailerId || grantMutation.isPending || retailersQuery.isError}
            >
              Add grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add portal member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Search user</Label>
              <Input
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setSelectedUserId(null);
                }}
                placeholder="Name, email, or user ID"
              />
            </div>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {userSearchQuery.isLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching users…
                </div>
              ) : userSearchQuery.isError ? (
                <p className="text-sm text-red-600">
                  {queryErrorMessage(userSearchQuery.error, 'Failed to search users.')}
                </p>
              ) : (userSearchQuery.data?.data?.users ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No matching users found.</p>
              ) : (
                (userSearchQuery.data?.data?.users ?? []).map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    disabled={!user.eligible}
                    className={`w-full rounded border px-3 py-2 text-left text-sm ${
                      !user.eligible
                        ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-70'
                        : selectedUserId === user.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200'
                    }`}
                    onClick={() => user.eligible && setSelectedUserId(user.id)}
                  >
                    <div className="font-medium">{user.name}</div>
                    <div className="text-muted-foreground">{user.email} · #{user.id}</div>
                    {!user.eligible && user.ineligibleReason ? (
                      <div className="mt-1 text-xs text-red-600">{user.ineligibleReason}</div>
                    ) : null}
                  </button>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Assigns RetailerViewer and sets access mode to portal_external.
            </p>
          </div>
          <DialogFooter>
            {membershipMutation.error instanceof Error ? (
              <p className="mr-auto text-sm text-red-600">{membershipMutation.error.message}</p>
            ) : null}
            <Button
              onClick={() => selectedUserId && membershipMutation.mutate(selectedUserId)}
              disabled={!selectedUserId || membershipMutation.isPending}
            >
              Add member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
